// features/projects/templates/continuum/cascade-grids.js
// ═══════════════════════════════════════════════════════════
// INTERVENTION GRIDS VIEWER — Section 4 of Cascade tab
//
// Renders Korea (9 spirals × 4 layers = 36 cells) and Atlantic
// cod (5 spirals × 4 sublayers = 20 cells) as side-by-side grid
// matrices with cell engagement coding.
//
// Headline finding visualized: same arrest-failure architecture
// (Korea 1/4 layers strong, Cod 2/4 sublayers strong; both
// Cultural-absent; both stabilization-at-lower).
//
// Data: lib/cascade/grids.js
// ═══════════════════════════════════════════════════════════

import { useState, useMemo } from 'react';
import { C, FONT as F } from './tokens';
import { GRIDS, ENGAGEMENT_LEVELS, gridSummary } from '../../../../lib/cascade/grids';

export default function CascadeGrids() {
  const [activeId, setActiveId] = useState(GRIDS[0].id || 'korea');
  const activeGrid = useMemo(
    () => GRIDS.find((g) => g.caseId === activeId) || GRIDS[0],
    [activeId]
  );

  return (
    <section style={{ marginBottom: 48 }}>
      <Header />
      <CaseTabs cases={GRIDS} activeId={activeGrid.caseId} onChange={setActiveId} />
      <Grid grid={activeGrid} />
      <Legend />
    </section>
  );
}

// ─── Header ─────────────────────────────────────────────────────────
function Header() {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 6 }}>
        <span style={{ fontSize: 11, color: C.dim, fontFamily: F.mono, letterSpacing: '0.06em' }}>04</span>
        <span style={{ fontFamily: F.serif, fontSize: 22, fontWeight: 400, color: C.text }}>Intervention Grids</span>
      </div>
      <div style={{ fontSize: 12, color: C.mid, lineHeight: 1.5, maxWidth: 800 }}>
        Spirals × intervention layers. Per cell: engagement level + what has been tried at that intersection.
        H-Continuum-10 prediction: cascades arrest only when ≥3 of 4 layers show measurable activation. Below
        that threshold, decline continues regardless of magnitude within engaged layers.
      </div>
    </div>
  );
}

// ─── Case tabs ───────────────────────────────────────────────────────
function CaseTabs({ cases, activeId, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 0, marginBottom: 0, borderBottom: `1px solid ${C.b1}` }}>
      {cases.map((c) => {
        const active = c.caseId === activeId;
        const color = C[c.caseColorKey] || C.gold;
        const summary = gridSummary(c);
        return (
          <button
            key={c.caseId}
            onClick={() => onChange(c.caseId)}
            style={{
              flex: '0 0 auto',
              padding: '12px 20px',
              background: active ? hexAlpha(color, 0.08) : 'transparent',
              border: 'none',
              borderBottom: active ? `2px solid ${color}` : `2px solid transparent`,
              color: active ? color : C.static,
              cursor: 'pointer',
              fontFamily: F.sans,
              textAlign: 'left',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2 }}>
              {c.caseLabel}
            </div>
            <div style={{ fontSize: 10, color: active ? color : C.dim, fontFamily: F.mono, letterSpacing: '0.04em' }}>
              {c.spirals.length} spirals · {c.layers.length} {c.substrate === 'eco' ? 'sublayers' : 'layers'} · {summary.strongCount}/{c.layers.length} strong-engaged
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ─── Grid (the matrix) ───────────────────────────────────────────────
function Grid({ grid }) {
  const [hoverCell, setHoverCell] = useState(null);
  const summary = useMemo(() => gridSummary(grid), [grid]);
  const caseColor = C[grid.caseColorKey] || C.gold;

  // Grid columns: spiral label (220px) + N layer columns (1fr each)
  const gridTemplate = `220px repeat(${grid.layers.length}, 1fr)`;

  return (
    <div
      style={{
        background: C.s2,
        border: `1px solid ${C.b1}`,
        borderRadius: '0 0 4px 4px',
      }}
    >
      {/* Layer header row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: gridTemplate,
          background: C.s1,
          borderBottom: `1px solid ${C.b1}`,
        }}
      >
        <div style={{ padding: '12px 16px', fontSize: 10, color: C.dim, fontFamily: F.mono, letterSpacing: '0.06em' }}>
          SPIRAL ↓ · LAYER →
        </div>
        {grid.layers.map((layer, idx) => {
          const engagement = summary.layerEngagement[layer.id];
          const engColor = engagement === 'strong' ? C.sage : engagement === 'partial' ? C.gold : C.dim;
          return (
            <div
              key={layer.id}
              style={{
                padding: '12px 16px',
                fontSize: 12,
                color: C.text,
                fontFamily: F.sans,
                fontWeight: 500,
                borderRight: idx === grid.layers.length - 1 ? 'none' : `1px solid ${C.b1}`,
              }}
            >
              <div style={{ marginBottom: 4 }}>{layer.label}</div>
              <div style={{ fontSize: 9, color: engColor, fontFamily: F.mono, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                {engagement === 'strong' ? '★ ' : ''}{engagement}-engaged
              </div>
            </div>
          );
        })}
      </div>

      {/* Spiral rows */}
      {grid.spirals.map((spiral, sIdx) => (
        <div
          key={spiral.id}
          style={{
            display: 'grid',
            gridTemplateColumns: gridTemplate,
            borderBottom: sIdx === grid.spirals.length - 1 ? 'none' : `1px solid ${C.b1}`,
          }}
        >
          {/* Spiral label cell */}
          <div
            style={{
              padding: '12px 16px',
              background: C.s1,
              borderRight: `1px solid ${C.b1}`,
            }}
          >
            <div style={{ fontSize: 10, color: caseColor, fontFamily: F.mono, letterSpacing: '0.06em', marginBottom: 3 }}>
              {spiral.id}
            </div>
            <div style={{ fontSize: 12, color: C.text, fontFamily: F.sans, fontWeight: 500, marginBottom: 3 }}>
              {spiral.label}
            </div>
            <div style={{ fontSize: 10, color: C.dim, fontFamily: F.mono, lineHeight: 1.4 }}>
              {spiral.short}
            </div>
          </div>

          {/* Cell columns */}
          {grid.layers.map((layer, lIdx) => {
            const cellId = `${spiral.id}.${layer.id}`;
            const cell = grid.cells[cellId] || { level: 'none' };
            const level = ENGAGEMENT_LEVELS[cell.level] || ENGAGEMENT_LEVELS.none;
            const cellColor = C[level.colorKey] || C.dim;
            const isLastCol = lIdx === grid.layers.length - 1;
            const isHover = hoverCell === cellId;

            return (
              <div
                key={cellId}
                onMouseEnter={() => setHoverCell(cellId)}
                onMouseLeave={() => setHoverCell(null)}
                style={{
                  padding: '12px 14px',
                  borderRight: isLastCol ? 'none' : `1px solid ${C.b1}`,
                  background: isHover ? hexAlpha(cellColor, 0.06) : 'transparent',
                  transition: 'background 0.12s',
                  cursor: 'default',
                }}
              >
                <CellBadge level={cell.level} colorKey={level.colorKey} />
                {cell.note && (
                  <div style={{ fontSize: 11, color: C.mid, lineHeight: 1.45, marginTop: 6 }}>
                    {cell.note}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {/* Summary footer */}
      <SummaryFooter grid={grid} summary={summary} />
    </div>
  );
}

// ─── Cell Badge ──────────────────────────────────────────────────────
function CellBadge({ level, colorKey }) {
  const color = C[colorKey] || C.dim;
  const filled = level === 'strong';
  const meta = ENGAGEMENT_LEVELS[level] || ENGAGEMENT_LEVELS.none;
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 9,
        fontFamily: F.mono,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        padding: '2px 7px',
        borderRadius: 3,
        border: `1px solid ${color}`,
        background: filled ? hexAlpha(color, 0.15) : 'transparent',
        color: color,
      }}
    >
      {meta.label}
    </span>
  );
}

// ─── Summary footer (layer engagement counts) ────────────────────────
function SummaryFooter({ grid, summary }) {
  const layerWord = grid.substrate === 'eco' ? 'sublayer' : 'layer';
  const verdict = summary.strongCount >= 3
    ? { color: C.sage, label: 'Multi-layer compound — H-10 arrest threshold met' }
    : summary.strongCount === 2
      ? { color: C.gold, label: `2-${layerWord} engagement — partial; below H-10 arrest threshold` }
      : summary.strongCount === 1
        ? { color: C.terra, label: `1-${layerWord} maximization — H-10 predicts continued cascade` }
        : { color: C.terra, label: `0-${layerWord} engagement — uncontrolled spiral predicted` };

  return (
    <div
      style={{
        padding: '14px 18px',
        background: C.s1,
        borderTop: `1px solid ${C.b1}`,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 18,
      }}
    >
      <div style={{ fontSize: 10, color: C.dim, fontFamily: F.mono, letterSpacing: '0.06em' }}>
        ENGAGEMENT SUMMARY
      </div>
      {grid.layers.map((layer) => {
        const eng = summary.layerEngagement[layer.id];
        const counts = summary.counts[layer.id];
        const color = eng === 'strong' ? C.sage : eng === 'partial' ? C.gold : C.dim;
        return (
          <div key={layer.id} style={{ fontSize: 11, color: C.mid, fontFamily: F.mono, letterSpacing: '0.04em' }}>
            <span style={{ color: C.static, marginRight: 4 }}>{layer.label.toUpperCase()}:</span>
            <span style={{ color }}>{eng === 'strong' ? '★ ' : ''}{eng}</span>
            <span style={{ color: C.dim, marginLeft: 6 }}>({counts.strong}/{counts.partial}/{counts.none}{counts.tbd > 0 ? `/${counts.tbd}TBD` : ''})</span>
          </div>
        );
      })}
      <div style={{ marginLeft: 'auto', fontSize: 11, color: verdict.color, fontFamily: F.mono, letterSpacing: '0.04em' }}>
        {verdict.label}
      </div>
    </div>
  );
}

// ─── Legend ──────────────────────────────────────────────────────────
function Legend() {
  return (
    <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, fontSize: 10, color: C.dim, fontFamily: F.mono }}>
      <span style={{ letterSpacing: '0.06em' }}>LEGEND:</span>
      {Object.entries(ENGAGEMENT_LEVELS).map(([key, meta]) => {
        const color = C[meta.colorKey] || C.dim;
        return (
          <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{
              fontSize: 9,
              fontFamily: F.mono,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              padding: '2px 6px',
              borderRadius: 3,
              border: `1px solid ${color}`,
              color,
              background: key === 'strong' ? hexAlpha(color, 0.15) : 'transparent',
            }}>
              {meta.label}
            </span>
            <span style={{ color: C.static }}>{meta.short}</span>
          </span>
        );
      })}
      <span style={{ marginLeft: 'auto', color: C.dim, letterSpacing: '0.04em' }}>
        Footer counts: strong/partial/none(/TBD)
      </span>
    </div>
  );
}

function hexAlpha(hex, alpha) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
